import Link from 'next/link';
import AmexIcon from '@discover-ui/icons/amex.svg';
import MapMarkerIcon from '@discover-ui/icons/map-marker.svg';
import KnifeForkIcon from '@discover-ui/icons/knife-fork.svg';
import DEFAULT_SESSION from '@discover-data/stores/restaurant/model/session/default';
import { sessionSlugs } from '@discover-lib/getCitySlug';
import { type RestaurantDetails } from '@discover-data/stores/restaurant/gql/getters/restaurantDetails';
import FrequentFoodiesLink from '@/components/molecules/FrequentFoodiesLink';
import Experiment from '@/components/analytics/experiments/experiment';
import {
  getDietaryBadges,
  PD6616_DIETARY_BADGES_FLAG,
  PD6616DietaryBadges,
} from '@/components/analytics/experiments/PD6616DietaryBadges';
import Root, { type RootProps } from './root';

export type RestaurantCardSubtitleRowProps = Pick<
  RestaurantDetails,
  'region' | 'suburb' | 'tags'
> & {
  acceptsAmex?: boolean;
  session: SessionType;
  points?: string;
  mobileRating?: React.ReactNode;
} & Omit<RootProps, 'id' | 'children'>;

function PointsLink({ points }: { points: string }) {
  return (
    <FrequentFoodiesLink
      points={points}
      layout="inline"
      className="text-ds-gray-light-700"
      iconClassName="text-ds-gray-light-700"
    />
  );
}

export default function RestaurantCardSubtitleRow({
  acceptsAmex,
  region,
  session,
  suburb,
  tags,
  points,
  mobileRating,
  ...props
}: RestaurantCardSubtitleRowProps) {
  const location = suburb || region;
  const cleanTags = (tags?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => node != null);
  const cuisine = cleanTags.find(({ category }) => category === 'Cuisine')?.title;
  const dietaryBadges = getDietaryBadges(tags);
  const sessionSlug = session !== DEFAULT_SESSION ? `/${sessionSlugs[session]}` : '';
  const locationTitle = location?.menuTitle;
  const hasPoints = points !== undefined && +points > 0;
  const showMobileMeta = !!mobileRating || hasPoints;
  const hasSubtitle = !!cuisine || !!locationTitle || !!acceptsAmex || showMobileMeta;

  if (!hasSubtitle) {
    if (dietaryBadges.length === 0) return null;
    // Control must keep rendering nothing here, so the row only exists in the treatment.
    return (
      <Experiment
        flag={PD6616_DIETARY_BADGES_FLAG}
        eligible
        control={null}
        treatment={
          <Root {...props}>
            <PD6616DietaryBadges badges={dietaryBadges} />
          </Root>
        }
      />
    );
  }

  return (
    <Root {...props}>
      <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
        {locationTitle && (
          <span className="inline-flex min-w-0 items-center gap-1">
            <MapMarkerIcon
              width={12}
              height={14}
              className="hidden shrink-0 text-ds-gray-light-700 md:block"
              aria-hidden="true"
            />
            <Link
              href={location?.slug ? `${location.slug}${sessionSlug}` : '#'}
              className="text-ds-gray-light-700 no-underline hover:underline">
              {locationTitle}
            </Link>
          </span>
        )}
        {locationTitle && cuisine && <span aria-hidden="true">&middot;</span>}
        {cuisine && (
          <span className="inline-flex min-w-0 items-center gap-1">
            <KnifeForkIcon
              width={12}
              height={14}
              className="hidden shrink-0 text-ds-gray-light-700 md:block"
              aria-hidden="true"
            />
            <span>{cuisine}</span>
          </span>
        )}
        <Experiment
          flag={PD6616_DIETARY_BADGES_FLAG}
          eligible={dietaryBadges.length > 0}
          control={null}
          treatment={
            <>
              {(!!locationTitle || !!cuisine) && <span aria-hidden="true">&middot;</span>}
              <PD6616DietaryBadges badges={dietaryBadges} />
              {!cuisine && !locationTitle && (hasPoints || acceptsAmex) && (
                <span
                  aria-hidden="true"
                  className={acceptsAmex && !hasPoints ? undefined : 'hidden md:inline'}>
                  &middot;
                </span>
              )}
            </>
          }
        />
        {hasPoints && (
          <span className="hidden items-center gap-x-1.5 md:inline-flex">
            {(cuisine || locationTitle) && <span aria-hidden="true">&middot;</span>}
            <PointsLink points={points ?? ''} />
          </span>
        )}
        {acceptsAmex && (
          <>
            {(cuisine || locationTitle || hasPoints) && <span aria-hidden="true">&middot;</span>}
            <AmexIcon width={40} />
          </>
        )}
      </span>
      {showMobileMeta && (
        <span className="flex flex-nowrap items-center gap-x-1.5 md:hidden">
          {mobileRating}
          {!!mobileRating && hasPoints && <span aria-hidden="true">&middot;</span>}
          {hasPoints && <PointsLink points={points ?? ''} />}
        </span>
      )}
    </Root>
  );
}
